/**
 * TOSS Web Application - useUnsavedChangesGuard Hook
 *
 * Protects users from accidentally losing unsaved form data.
 *
 * Features:
 * 1. Browser beforeunload event - Shows native browser warning on refresh/close
 * 2. React Router navigation blocker - Shows custom dialog on in-app navigation
 *
 * Usage:
 * ```tsx
 * const blocker = useUnsavedChangesGuard(formState.isDirty);
 * return (
 *   <>
 *     <form>...</form>
 *     <UnsavedChangesDialog blocker={blocker} />
 *   </>
 * );
 * ```
 */

import { useCallback, useEffect } from 'react';
import { useBlocker, type Blocker, type Location } from 'react-router-dom';

/**
 * Marks a navigation the guard must NOT hold.
 *
 * Asking «выйти без сохранения?» assumes the customer chose to leave. Some navigations are not
 * that: the portal drops the current screen when the active legal entity changes under it (from
 * the switcher or from a `CompanySwitched` push in another tab), and a half-typed draft belongs
 * to the company it was typed for — keeping it is the bug, not losing it. There is nothing to
 * ask, and a dialog there would sit over a screen whose data has already been dropped.
 */
export const FORCED_LEAVE_STATE = { forcedLeave: true } as const;

/** Whether this navigation carries {@link FORCED_LEAVE_STATE}. */
export function isForcedLeave(location: Pick<Location, 'state'>): boolean {
  return (location.state as { forcedLeave?: unknown } | null)?.forcedLeave === true;
}

export function useUnsavedChangesGuard(hasChanges: boolean): Blocker {
  // 1. Browser beforeunload (refresh/close tab)
  useEffect(() => {
    if (!hasChanges) return;

    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  // 2. React Router navigation blocker
  /**
   * Memoised, and that is load-bearing rather than tidy.
   *
   * `useBlocker` re-registers its function whenever the identity changes, and an inline arrow
   * changes on every render. Re-registering while a blocked POP is waiting for `proceed()` loses
   * the `proceeding` state the router uses to let that navigation through — so «Выйти» left the
   * customer exactly where they were. Found on the stand; the arrow (a PUSH) never showed it.
   */
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
      hasChanges &&
      // Only a change of SCREEN. A sheet opening on the same path is a query parameter, and
      // holding that would put this dialog over every filter the customer opens.
      currentLocation.pathname !== nextLocation.pathname &&
      !isForcedLeave(nextLocation),
    [hasChanges]
  );

  const blocker = useBlocker(shouldBlock);

  return blocker;
}
